/**
 * NodeConfigPanel — Right-side configuration panel for selected flow nodes
 *
 * Opens when a user clicks a node on the canvas.
 * Shows editable fields specific to the node type.
 */

import React from 'react';
import { X, Trash2, Brain, Wrench, Database, FileText, GitBranch, Play } from 'lucide-react';
import { NODE_COLORS } from './FlowNodes';

const TYPE_META = {
  start:      { label: 'Start Node', icon: Play, colors: NODE_COLORS.start },
  agent:      { label: 'AI Agent', icon: Brain, colors: NODE_COLORS.agent },
  tool:       { label: 'Tool', icon: Wrench, colors: NODE_COLORS.tool },
  dataSource: { label: 'Data Source', icon: Database, colors: NODE_COLORS.dataSource },
  output:     { label: 'Output', icon: FileText, colors: NODE_COLORS.output },
  condition:  { label: 'Condition', icon: GitBranch, colors: NODE_COLORS.condition },
};

function Field({ label, children }) {
  return (
    <div className="mb-3">
      <label className="block text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#555' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, multiline = false }) {
  const style = {
    background: '#0A0A0A',
    border: '1px solid #1A1A1A',
    color: '#F5F5F5',
  };

  if (multiline) {
    return (
      <textarea
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        className="w-full px-3 py-2 rounded-lg text-xs outline-none resize-none focus:border-[#E8B800] transition-colors"
        style={style}
      />
    );
  }

  return (
    <input
      type="text"
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 rounded-lg text-xs outline-none focus:border-[#E8B800] transition-colors"
      style={style}
    />
  );
}

function SelectInput({ value, onChange, options }) {
  return (
    <select
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      className="w-full px-3 py-2 rounded-lg text-xs outline-none focus:border-[#E8B800] transition-colors"
      style={{
        background: '#0A0A0A',
        border: '1px solid #1A1A1A',
        color: '#F5F5F5',
      }}
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
}

// Config forms for each node type
function StartConfig({ data, onChange }) {
  return (
    <>
      <Field label="Trigger Type">
        <SelectInput
          value={data.triggerType}
          onChange={v => onChange({ triggerType: v })}
          options={[
            { value: 'Manual trigger', label: 'Manual (click Run)' },
            { value: 'Schedule', label: 'Scheduled' },
            { value: 'Event', label: 'On event' },
          ]}
        />
      </Field>
    </>
  );
}

function AgentConfig({ data, onChange }) {
  return (
    <>
      <Field label="Agent Name">
        <TextInput
          value={data.label}
          onChange={v => onChange({ label: v })}
          placeholder="e.g. Scouting Analyst"
        />
      </Field>
      <Field label="Description">
        <TextInput
          value={data.description}
          onChange={v => onChange({ description: v })}
          placeholder="What does this agent do?"
          multiline
        />
      </Field>
      <Field label="Model">
        <SelectInput
          value={data.model}
          onChange={v => onChange({ model: v })}
          options={[
            { value: 'Gemini 3 Flash', label: 'Gemini 3 Flash (fast)' },
            { value: 'Gemini 2.5 Pro', label: 'Gemini 2.5 Pro (reasoning)' },
            { value: 'Claude Opus', label: 'Claude Opus (deep)' },
          ]}
        />
      </Field>
      <Field label="System Instructions">
        <TextInput
          value={data.systemPrompt}
          onChange={v => onChange({ systemPrompt: v })}
          placeholder="Custom instructions for this agent..."
          multiline
        />
      </Field>
    </>
  );
}

function ToolConfig({ data, onChange }) {
  return (
    <>
      <Field label="Tool Name">
        <TextInput value={data.label} onChange={v => onChange({ label: v })} placeholder="Tool name" />
      </Field>
      <Field label="Description">
        <TextInput
          value={data.description}
          onChange={v => onChange({ description: v })}
          placeholder="What this tool does"
          multiline
        />
      </Field>
      {data.toolId && (
        <div className="mt-2 px-3 py-2 rounded-lg" style={{ background: '#0088FF08', border: '1px solid #0088FF20' }}>
          <p className="text-[9px] uppercase tracking-wider mb-1" style={{ color: '#0088FF80' }}>Tool ID</p>
          <p className="text-[11px] font-mono" style={{ color: '#0088FF' }}>{data.toolId}</p>
        </div>
      )}
    </>
  );
}

function DataSourceConfig({ data, onChange }) {
  return (
    <>
      <Field label="Source Name">
        <TextInput value={data.label} onChange={v => onChange({ label: v })} placeholder="Data source name" />
      </Field>
      <Field label="Description">
        <TextInput
          value={data.description}
          onChange={v => onChange({ description: v })}
          placeholder="What data this provides"
          multiline
        />
      </Field>
    </>
  );
}

function OutputConfig({ data, onChange }) {
  return (
    <>
      <Field label="Output Name">
        <TextInput value={data.label} onChange={v => onChange({ label: v })} placeholder="Output name" />
      </Field>
      <Field label="Format">
        <SelectInput
          value={data.format}
          onChange={v => onChange({ format: v })}
          options={[
            { value: 'chat', label: 'Chat Response' },
            { value: 'pdf', label: 'PDF Report' },
            { value: 'email', label: 'Email' },
            { value: 'video', label: 'Video Package' },
          ]}
        />
      </Field>
    </>
  );
}

function ConditionConfig({ data, onChange }) {
  return (
    <>
      <Field label="Condition Name">
        <TextInput value={data.label} onChange={v => onChange({ label: v })} placeholder="e.g. Is Baseball?" />
      </Field>
      <Field label="Condition Logic">
        <TextInput
          value={data.condition}
          onChange={v => onChange({ condition: v })}
          placeholder="e.g. sport === 'baseball'"
          multiline
        />
      </Field>
    </>
  );
}

const CONFIG_FORMS = {
  start: StartConfig,
  agent: AgentConfig,
  tool: ToolConfig,
  dataSource: DataSourceConfig,
  output: OutputConfig,
  condition: ConditionConfig,
};

export default function NodeConfigPanel({ node, onUpdate, onDelete, onClose }) {
  if (!node) return null;

  const meta = TYPE_META[node.type] || TYPE_META.agent;
  const Icon = meta.icon;
  const ConfigForm = CONFIG_FORMS[node.type];

  const handleChange = (updates) => {
    onUpdate(node.id, { ...node.data, ...updates });
  };

  return (
    <div
      className="w-[280px] flex-shrink-0 flex flex-col h-full overflow-hidden rounded-xl"
      style={{ background: '#050505', border: '1px solid #1A1A1A' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-3 border-b" style={{ borderColor: '#1A1A1A' }}>
        <div className="flex items-center gap-2">
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center"
            style={{ background: meta.colors.accent + '20' }}
          >
            <Icon size={14} style={{ color: meta.colors.accent }} />
          </div>
          <div>
            <p className="text-xs font-medium" style={{ color: '#F5F5F5' }}>{meta.label}</p>
            <p className="text-[9px]" style={{ color: '#555' }}>Node #{node.id.slice(0, 8)}</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg transition-colors hover:bg-white/[0.04]"
        >
          <X size={14} style={{ color: '#555' }} />
        </button>
      </div>

      {/* Config form */}
      <div className="flex-1 overflow-auto px-3 py-3">
        {ConfigForm && <ConfigForm data={node.data} onChange={handleChange} />}
      </div>

      {/* Footer */}
      <div className="px-3 py-2 border-t flex justify-between items-center" style={{ borderColor: '#1A1A1A' }}>
        <button
          onClick={() => onDelete(node.id)}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] transition-colors hover:bg-red-500/10"
          style={{ color: '#FF4444' }}
        >
          <Trash2 size={12} />
          Delete
        </button>
        <button
          onClick={onClose}
          className="px-3 py-1.5 rounded-lg text-[11px] font-medium"
          style={{ background: '#E8B800', color: '#000' }}
        >
          Done
        </button>
      </div>
    </div>
  );
}
